import { useLayoutEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import CampaignDialog from "./CampaignDialog.js";
import { useCampaignPhase } from "./campaign.js";

// Composed into --top-offset (index.js GlobalStyles, alongside the safe-area inset) and
// read from there by index.js (fixed AppBars), ResponsiveDrawer (both drawers), Spa
// (viewport height) and RootLayout (toast offset) to shift the rest of the app down by
// exactly the banner's height. Stays at 0px whenever no campaign is running, so all of
// those rules are no-ops between campaigns.
const CSS_VAR = "--campaign-banner-height";

// Fixed height rather than a measured one: the copy is short enough to never wrap, and
// a constant means the CSS variable is written once instead of on every layout pass.
// If the copy ever grows long enough to wrap, bump this to match.
const BANNER_HEIGHT_PX = 40;

// Full-width, undismissible bar shown only while a campaign is live. Publishes its height
// as a CSS variable (see CSS_VAR above), with an in-flow spacer pushing page content down
// to match. Tapping it opens the campaign dialog.
const CampaignBanner = () => {
  const { phase } = useCampaignPhase();
  const isLive = phase === "live";
  const [dialogOpen, setDialogOpen] = useState(false);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      CSS_VAR,
      isLive ? `${BANNER_HEIGHT_PX}px` : "0px",
    );

    return () => document.documentElement.style.setProperty(CSS_VAR, "0px");
  }, [isLive]);

  if (!isLive) return null;

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setDialogOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setDialogOpen(true);
        }}
        sx={{
          position: "fixed",
          // Spans the safe-area top inset rather than starting below it, so the banner's
          // colour runs behind the status bar instead of leaving RootLayout's scrim
          // showing as a mismatched strip above it. Painting the inset here rather than
          // making the scrim campaign-aware keeps the scrim ignorant of what sits at the
          // top of the app — this banner's z-index (drawer + 1) is already above it, so
          // it simply covers it. 0px inset means this is identical to the old behaviour.
          top: 0,
          paddingTop: "var(--safe-top)",
          left: 0,
          right: 0,
          // border-box, so the inset is added to (not taken out of) the banner's own
          // height, leaving BANNER_HEIGHT_PX of content area to centre copy within.
          height: `calc(${BANNER_HEIGHT_PX}px + var(--safe-top))`,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "primary.main",
          // Hints the compositor to give the banner its own GPU layer up front, so iOS
          // doesn't reposition it in jumps during rubber-band scrolling. translateZ(0)
          // forces the same promotion immediately; will-change asks for it ahead of the
          // scroll starting, which is the part that was missing.
          willChange: "transform",
          transform: "translateZ(0)",
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        {/* noWrap so unexpectedly long copy ellipsises rather than breaking the
        fixed height the rest of the layout is offset by */}
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, color: "primary.contrastText" }}
        >
          {/* Campaign copy — overwrite for each campaign */}
          Something's happening at Curate today!
          {/* Inline rather than a flex child so noWrap's ellipsis still works.
          Sized slightly above the body2 text, matching SidebarPrompt's treatment. */}
          <CampaignIcon
            sx={{ fontSize: "1rem", verticalAlign: "text-bottom", ml: 0.5 }}
          />
        </Typography>
      </Box>

      {/* In-flow spacer — pushes page content down by the banner's height */}
      <Box sx={{ height: `${BANNER_HEIGHT_PX}px` }} />

      <CampaignDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

export default CampaignBanner;
